import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Copy, ExternalLink, Video } from "lucide-react";
import { hubListVideos, hubUploadVideo, hubDeleteVideo } from "@/lib/adminApi";

const HubManager = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [slug, setSlug] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await hubListVideos();
      setVideos(d.videos || []);
    } catch {
      toast.error("Kon video's niet laden");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Geef een titel op");
    if (!file) return toast.error("Kies een videobestand");
    const fd = new FormData();
    fd.append("title", title);
    fd.append("description", description);
    fd.append("slug", slug);
    fd.append("file", file);
    setUploading(true);
    setProgress(0);
    try {
      await hubUploadVideo(fd, setProgress);
      toast.success("Video toegevoegd");
      setTitle(""); setDescription(""); setSlug(""); setFile(null);
      document.getElementById("hub-file-input").value = "";
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Uploaden mislukt");
    }
    setUploading(false);
    setProgress(0);
  };

  const remove = async (v) => {
    if (!window.confirm(`Video "${v.title}" verwijderen?`)) return;
    try {
      await hubDeleteVideo(v.id);
      toast.success("Video verwijderd");
      setVideos((prev) => prev.filter((x) => x.id !== v.id));
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  const publicUrl = (v) => `${window.location.origin}/hub/${v.slug}`;

  const copyLink = (v) => {
    navigator.clipboard.writeText(publicUrl(v));
    toast.success("Link gekopieerd");
  };

  return (
    <div className="space-y-6" data-testid="hub-manager">
      <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-5" data-testid="hub-upload-form">
        <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#0a5a3c]">
          <Video className="h-4 w-4" /> Nieuwe video toevoegen
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Titel *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="hub-title-input"
              placeholder="bv. Griepvaccinatie uitleg"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0a5a3c]" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Link-naam (optioneel)</label>
            <input value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="hub-slug-input"
              placeholder="wordt automatisch gemaakt van de titel"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0a5a3c]" />
          </div>
        </div>
        <div className="mt-4">
          <label className="mb-1 block text-xs font-medium text-slate-500">Beschrijving</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="hub-description-input"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0a5a3c]" />
        </div>
        <div className="mt-4">
          <label className="mb-1 block text-xs font-medium text-slate-500">Videobestand (MP4) *</label>
          <input id="hub-file-input" type="file" accept="video/mp4,video/quicktime,video/webm" data-testid="hub-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-[#0a5a3c]/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-[#0a5a3c]" />
        </div>
        {uploading && (
          <div className="mt-4" data-testid="hub-upload-progress">
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full bg-[#0a5a3c] transition-all" style={{ width: `${progress}%` }} />
            </div>
            <p className="mt-1 text-xs text-slate-500">{progress}% geüpload…</p>
          </div>
        )}
        <button type="submit" disabled={uploading} data-testid="hub-upload-submit"
          className="mt-5 flex items-center gap-2 rounded-lg bg-[#0a2540] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0a1f38] disabled:opacity-50">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? "Bezig met uploaden…" : "Video uploaden"}
        </button>
      </form>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="hub-videos-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Titel</th>
              <th className="px-4 py-3">Openbare link (voor NFC)</th>
              <th className="px-4 py-3">Acties</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={3} className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
            ) : videos.length === 0 ? (
              <tr><td colSpan={3} className="px-4 py-10 text-center text-slate-400">Nog geen video's</td></tr>
            ) : videos.map((v) => (
              <tr key={v.id} data-testid={`hub-row-${v.slug}`} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-[#0a2540]">{v.title}</p>
                  {v.description && <p className="mt-0.5 max-w-xs truncate text-xs text-slate-500">{v.description}</p>}
                </td>
                <td className="px-4 py-3">
                  <code className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-700">/hub/{v.slug}</code>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => copyLink(v)} data-testid={`hub-copy-${v.slug}`} title="Link kopiëren"
                      className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-[#0a2540] hover:bg-slate-50">
                      <Copy className="h-3.5 w-3.5" /> Kopieer
                    </button>
                    <a href={publicUrl(v)} target="_blank" rel="noreferrer" data-testid={`hub-open-${v.slug}`} title="Openen"
                      className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-[#0a5a3c] hover:bg-[#0a5a3c]/5">
                      <ExternalLink className="h-3.5 w-3.5" /> Bekijk
                    </a>
                    <button onClick={() => remove(v)} data-testid={`hub-delete-${v.slug}`} title="Verwijderen"
                      className="flex items-center gap-1.5 rounded-lg border border-rose-200 px-2.5 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50">
                      <Trash2 className="h-3.5 w-3.5" /> Verwijder
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HubManager;
